export interface WidgetPageContext {
  current_url?: string
  pathname?: string
  route_name?: string
  entity_type?: string
  entity_id?: string
  entity_label?: string
  business_slug?: string
  metadata?: Record<string, unknown>
}

/** Page-owned entity descriptor (camelCase); merged into widget page_context at send time. */
export type LucaViewDescriptor = {
  entityType?: string
  entityId?: string
  entityLabel?: string
}

export function normalizeLucaViewDescriptor(
  descriptor: LucaViewDescriptor | null | undefined
): LucaViewDescriptor | null {
  if (!descriptor) return null
  const { entityType, entityId, entityLabel } = descriptor
  if (!entityType && !entityId && !entityLabel) return null
  return { entityType, entityId, entityLabel }
}

export function mergeLucaViewContext(
  base: WidgetPageContext,
  viewContext: LucaViewDescriptor | null | undefined
): WidgetPageContext {
  const descriptor = normalizeLucaViewDescriptor(viewContext)
  if (!descriptor) return base

  return {
    ...base,
    ...(descriptor.entityType ? { entity_type: descriptor.entityType } : {}),
    ...(descriptor.entityId ? { entity_id: descriptor.entityId } : {}),
    ...(descriptor.entityLabel ? { entity_label: descriptor.entityLabel } : {})
  }
}

const ROUTE_TO_ENTITY_TYPE: Record<string, string> = {
  accounts: 'accounts',
  accounting: 'periods',
  contacts: 'contacts',
  documents: 'documents',
  files: 'files',
  invoicing: 'invoices',
  products: 'products'
}

const RESERVED_ROOT_SEGMENTS = new Set([
  '',
  'start',
  'demo',
  'nuori-yrittaja',
  'stripe-redirect',
  'onboarding',
  'business',
  'mobile',
  'login'
])

export function normalizeBusinessSlug(businessSlug?: string | null): string | null {
  const normalized = String(businessSlug ?? '').trim()
  return normalized || null
}

export function getCurrentBusinessSlug(pathname: string): string | null {
  const firstSegment = pathname.split('/').filter(Boolean)[0] ?? ''
  if (!firstSegment || RESERVED_ROOT_SEGMENTS.has(firstSegment)) {
    return null
  }

  return firstSegment
}

function buildRouteName(pathname: string, businessSlug: string | null): string {
  const segments = pathname.split('/').filter(Boolean)
  if (businessSlug && segments[0] === businessSlug) {
    return segments[1] ?? 'business_root'
  }

  return segments[0] ?? 'root'
}

export function buildWidgetPageContext(params: {
  pathname: string
  search: string
  allBusinesses: boolean
  businessSlug?: string | null
}): WidgetPageContext {
  const businessSlug = normalizeBusinessSlug(params.businessSlug)
  const routeName = buildRouteName(params.pathname, businessSlug)
  const queryParams = new URLSearchParams(params.search)
  const entityType = ROUTE_TO_ENTITY_TYPE[routeName]
  const entityId = queryParams.get('id')?.trim() || undefined
  const idType = queryParams.get('idType')?.trim() || undefined
  const metadata =
    routeName === 'files' && idType
      ? {
          id_type: idType
        }
      : undefined

  return {
    current_url:
      typeof window === 'undefined'
        ? `${params.pathname}${params.search}`
        : `${window.location.origin}${params.pathname}${params.search}`,
    pathname: params.pathname,
    route_name: routeName,
    business_slug: params.allBusinesses ? undefined : businessSlug ?? undefined,
    entity_type: entityType,
    entity_id: entityType ? entityId : undefined,
    metadata
  }
}
